import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  Spinner,
  Tabs,
  Table,
  Modal,
  FormField,
  Input,
  Textarea,
  Select,
  Autosuggest,
  Checkbox,
  Flashbar,
  ColumnLayout,
  DatePicker,
  TextFilter,
} from "@amzn/awsui-components-react";

import {
  getRaisePage,
  upsertCourse,
  deleteCourse,
  upsertCell,
  upsertSession,
  deleteSession,
  upsertFaq,
  deleteFaq,
  upsertFaqCategory,
  deleteFaqCategory,
  upsertProvider,
  deleteProvider,
  upsertPlatform,
  deletePlatform,
  upsertSkillSet,
  deleteSkillSet,
  upsertContent,
  upsertContact,
  deleteContact,
} from "../api";
import MarkdownView from "../components/MarkdownView";
import { getSuggestedLoginsV2 } from "../../../utilities/papiAPIs";

/**
 * Tracks the in-flight write so buttons can show a spinner and disable while a
 * save/delete (and its follow-up reload) runs. { busy, busyId } — busyId marks
 * the specific inline row being deleted.
 */
const BusyContext = React.createContext({ busy: false, busyId: null });
const useBusy = () => React.useContext(BusyContext);

/** A field label with a red asterisk marking it as required. */
const RequiredLabel = ({ children }) => (
  <span>
    {children} <span style={{ color: "#d13212" }}>*</span>
  </span>
);

/**
 * A session's state is derived from its date, never stored: a date before today
 * is DELIVERED, today or later is UPCOMING. Returns "" when no date is set yet.
 * Compared at day granularity in local time (the date picker has no time).
 */
const sessionState = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today ? "DELIVERED" : "UPCOMING";
};

/**
 * Next sort_order for a new row in a group: max(sort_order) + 1, or 1 when the
 * group is empty. Derived in the UI from data already loaded, then prefilled
 * into the (editable) sort-order field.
 */
const nextSortOrder = (rows) => {
  const max = (rows || []).reduce(
    (m, r) => Math.max(m, Number(r.sort_order) || 0),
    0
  );
  return String(max + 1);
};

/** True when the value is a well-formed absolute https:// URL. */
const isValidHttpsUrl = (value) => {
  try {
    const u = new URL((value || "").trim());
    return u.protocol === "https:";
  } catch (e) {
    return false;
  }
};

/** Parse a stored role_order value (JSON array string) to an array of labels. */
const parseRoleOrder = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

/**
 * Client-side text filtering + column sorting for the admin tables. Returns the
 * filtered+sorted items, the props to spread onto an AWS-UI <Table>, and a
 * ready-to-render <TextFilter> for the table's `filter` slot.
 *
 * - Text filter: case-insensitive substring match across `filterFields` (the
 *   given fields) — defaults to every string/number field on the rows.
 * - Sort: columns opt in via `sortingField` / `sortingComparator`.
 */
function useTableTools(items, { filterFields, filterPlaceholder = "Find..." } = {}) {
  const [sortingColumn, setSortingColumn] = useState(null);
  const [sortingDescending, setSortingDescending] = useState(false);
  const [filterText, setFilterText] = useState("");

  const filteredItems = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) => {
      const fields =
        filterFields ||
        Object.keys(row).filter((k) => {
          const v = row[k];
          return typeof v === "string" || typeof v === "number";
        });
      return fields.some((f) => {
        const v = row[f];
        return v != null && String(v).toLowerCase().includes(q);
      });
    });
  }, [items, filterText, filterFields]);

  const sortedItems = useMemo(() => {
    if (!sortingColumn) return filteredItems;
    const field = sortingColumn.sortingField;
    const comparator =
      sortingColumn.sortingComparator ||
      ((a, b) => {
        const av = a?.[field];
        const bv = b?.[field];
        if (av == null && bv == null) return 0;
        if (av == null) return -1;
        if (bv == null) return 1;
        if (typeof av === "number" && typeof bv === "number") return av - bv;
        return String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    const out = [...filteredItems].sort(comparator);
    return sortingDescending ? out.reverse() : out;
  }, [filteredItems, sortingColumn, sortingDescending]);

  const filter = (
    <TextFilter
      filteringText={filterText}
      filteringPlaceholder={filterPlaceholder}
      filteringAriaLabel="Filter items"
      onChange={({ detail }) => setFilterText(detail.filteringText)}
      countText={
        filterText
          ? `${sortedItems.length} match${sortedItems.length === 1 ? "" : "es"}`
          : ""
      }
    />
  );

  return {
    items: sortedItems,
    filter,
    tableSortProps: {
      sortingColumn,
      sortingDescending,
      onSortingChange: ({ detail }) => {
        setSortingColumn(detail.sortingColumn);
        setSortingDescending(detail.isDescending);
      },
    },
  };
}

/**
 * RAISE admin surface (rendered inside the Admin panel, Support-Admin gated by
 * the caller). Sub-tabs for the editable entities: Courses, Calendar, FAQ and
 * Home Content. Every write goes through the api.js writers, which enforce
 * server-side Support/Portal admin authorization and bust the read cache.
 */
export default function RaiseAdmin() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState([]);
  // A write (save/delete) is in flight. Used to show button spinners and lock
  // out further clicks until the mutation + reload finish. `busyId` marks the
  // specific row being deleted so only its inline button spins.
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Pending confirmation ("Are you sure?") before a write runs. When set, the
  // confirm modal is shown; `resolve` is the promise resolver awaited by
  // runWrite (true = proceed, false = cancelled).
  const [confirmState, setConfirmState] = useState(null);

  // showSpinner: full-page spinner (initial load only). Post-write reloads pass
  // false so the table/modal stay put while the button shows its own spinner.
  // Returns true when the reload succeeded, false when it failed. Callers that
  // reload after a write use this so a "success" toast isn't shown over a
  // reload that actually failed (leaving stale data on screen).
  const load = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const result = await getRaisePage();
      setPage(result || {});
      return true;
    } catch (e) {
      setFlash([{ type: "error", content: e?.errors?.[0]?.message || "Failed to load RAISE data.", dismissible: true, onDismiss: () => setFlash([]) }]);
      return false;
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (type, content) =>
    setFlash([{ type, content, dismissible: true, onDismiss: () => setFlash([]) }]);

  // Opens the "Are you sure?" modal and resolves true/false with the choice.
  const askConfirm = (message) =>
    new Promise((resolve) => setConfirmState({ message, resolve }));

  const resolveConfirm = (ok) => {
    setConfirmState((s) => {
      if (s && s.resolve) s.resolve(ok);
      return null;
    });
  };

  // Run a write with a busy flag so the triggering button can show a spinner
  // and stay disabled through both the mutation and the follow-up reload.
  // `rowId` (optional) scopes the busy state to a specific inline (delete) row.
  // `confirm` (optional) — when a message string is given, an "Are you sure?"
  // dialog is shown first and the write only runs if the admin confirms.
  const runWrite = async (fn, okMsg, rowId = null, confirm = null) => {
    if (busy) return false; // guard against double-submits
    if (confirm) {
      const proceed = await askConfirm(confirm);
      if (!proceed) return false;
    }
    setBusy(true);
    setBusyId(rowId);
    try {
      const res = await fn();
      if (res && res.error) {
        notify("error", res.error);
        return false;
      }
      // The write committed server-side — report success now and let the editor
      // close, so a failed follow-up reload can't strand the admin on an open
      // modal and tempt a resubmit (which would create a duplicate). The reload
      // is best-effort; if it fails, load() surfaces its own error flash.
      notify("success", okMsg);
      await load({ showSpinner: false });
      return true;
    } catch (e) {
      notify("error", e?.errors?.[0]?.message || "Operation failed.");
      return false;
    } finally {
      setBusy(false);
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Box padding="l">
        <Spinner /> Loading RAISE admin...
      </Box>
    );
  }

  const curriculum = page?.curriculum || {};
  const calendar = page?.calendar || {};
  const faq = page?.faq || {};
  // Generic content map { section: { key: { value, type } } }; the Home editor
  // uses the "home" section.
  const homeContent = page?.content?.home || {};
  const contacts = page?.contacts || [];
  // Admin-configured Contact Us section (role) order — a JSON array of role
  // labels stored under content section 'contacts', key 'role_order'. Parsed
  // inline (not a hook — this runs after the early `loading` return).
  const contactRoleOrder = parseRoleOrder(page?.content?.contacts?.role_order?.value);

  return (
    <BusyContext.Provider value={{ busy, busyId }}>
    <SpaceBetween size="l">
      {flash.length > 0 ? <Flashbar items={flash} /> : null}

      {confirmState ? (
        <Modal
          visible
          onDismiss={() => resolveConfirm(false)}
          header="Are you sure?"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => resolveConfirm(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => resolveConfirm(true)}>
                  Confirm
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {confirmState.message}
        </Modal>
      ) : null}

      <Tabs
        tabs={[
          {
            id: "home",
            label: "Home",
            content: <HomeAdmin content={homeContent} runWrite={runWrite} />,
          },
          {
            id: "courses",
            label: "Courses",
            content: <CoursesAdmin curriculum={curriculum} runWrite={runWrite} />,
          },
          {
            id: "calendar",
            label: "Calendar",
            content: <CalendarAdmin calendar={calendar} runWrite={runWrite} />,
          },
          {
            id: "faq",
            label: "FAQ",
            content: <FaqAdmin faq={faq} runWrite={runWrite} />,
          },
          {
            id: "providers",
            label: "Providers",
            content: (
              <LookupAdmin
                title="Providers"
                singular="provider"
                items={curriculum.providers || []}
                idKey="provider_id"
                nameKey="display_name"
                onUpsert={upsertProvider}
                onDelete={deleteProvider}
                runWrite={runWrite}
              />
            ),
          },
          {
            id: "platforms",
            label: "Platforms",
            content: (
              <LookupAdmin
                title="Platforms"
                singular="platform"
                items={calendar.platforms || []}
                idKey="platform_id"
                nameKey="display_name"
                onUpsert={upsertPlatform}
                onDelete={deletePlatform}
                runWrite={runWrite}
              />
            ),
          },
          {
            id: "skillsets",
            label: "Skill Sets",
            content: (
              <LookupAdmin
                title="Skill Sets"
                singular="skill set"
                items={curriculum.skill_sets || []}
                idKey="skill_set_id"
                nameKey="name"
                withSortOrder
                onUpsert={upsertSkillSet}
                onDelete={deleteSkillSet}
                runWrite={runWrite}
              />
            ),
          },
          {
            id: "faqcategories",
            label: "FAQ Categories",
            content: (
              <LookupAdmin
                title="FAQ Categories"
                singular="category"
                items={faq.categories || []}
                idKey="category_id"
                nameKey="name"
                withSortOrder
                onUpsert={upsertFaqCategory}
                onDelete={deleteFaqCategory}
                runWrite={runWrite}
              />
            ),
          },
          {
            id: "celltaglines",
            label: "Cell taglines",
            content: <MatrixAdmin curriculum={curriculum} runWrite={runWrite} />,
          },
          {
            id: "contacts",
            label: "Contacts",
            content: <ContactsAdmin contacts={contacts} roleOrder={contactRoleOrder} runWrite={runWrite} />,
          },
        ]}
      />
    </SpaceBetween>
    </BusyContext.Provider>
  );
}

/* ------------------------------------------------------------------- Home */
// Editable top prose for the public RAISE Home tab (shown above the flywheel
// SVG, which is not editable). A single config row, so this is an inline form
// rather than a table + modal. All fields are optional; a blank field falls
// back to the built-in default on the public page. `how_body` is Markdown.

function HomeAdmin({ content, runWrite }) {
  const { busy } = useBusy();
  // `content` is the "home" section of the generic map: { key: { value, type } }.
  const initial = (key) => content?.[key]?.value || "";
  const [form, setForm] = useState({
    page_title: initial("page_title"),
    page_subtitle: initial("page_subtitle"),
    intro_line: initial("intro_line"),
    how_heading: initial("how_heading"),
    how_body: initial("how_body"),
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Save all fields for the "home" section in one batched write. how_body is
  // markdown; the rest are plain text.
  const save = () =>
    runWrite(
      () =>
        upsertContent("home", [
          { key: "page_title", value: form.page_title, type: "text" },
          { key: "page_subtitle", value: form.page_subtitle, type: "text" },
          { key: "intro_line", value: form.intro_line, type: "text" },
          { key: "how_heading", value: form.how_heading, type: "text" },
          { key: "how_body", value: form.how_body, type: "markdown" },
        ]),
      "Home content saved.",
      null,
      "Save the home page content?"
    );

  return (
    <Container
      header={
        <Header
          variant="h3"
          description="Top-of-page copy for the public RAISE Home tab. The illustrated flywheel below it is not editable. Leave a field blank to use the built-in default."
          actions={
            <Button variant="primary" loading={busy} disabled={busy} onClick={save}>
              Save
            </Button>
          }
        >
          Home content
        </Header>
      }
    >
      <SpaceBetween size="s">
        <FormField label="Page title" description="Large gradient heading. Default: the full RAISE curriculum title.">
          <Input
            value={form.page_title}
            onChange={({ detail }) => set("page_title", detail.value)}
            placeholder="RAISE - Responsible AI Skills & Expertise Curriculum"
          />
        </FormField>
        <FormField label="Page subtitle">
          <Input
            value={form.page_subtitle}
            onChange={({ detail }) => set("page_subtitle", detail.value)}
            placeholder="RAISE the bar with AI"
          />
        </FormField>
        <FormField label="Intro line" description="Shown under the subtitle (plain text).">
          <Input
            value={form.intro_line}
            onChange={({ detail }) => set("intro_line", detail.value)}
            placeholder="Built in partnership between AI-TAG and the ATEAM."
          />
        </FormField>
        <FormField label="How-it-works heading">
          <Input
            value={form.how_heading}
            onChange={({ detail }) => set("how_heading", detail.value)}
            placeholder="How RAISE works"
          />
        </FormField>
        <FormField
          label="How-it-works body (Markdown)"
          description="Supports **bold**, lists, and [links](https://...)."
        >
          <Textarea
            value={form.how_body}
            onChange={({ detail }) => set("how_body", detail.value)}
            rows={5}
            placeholder="Describe how RAISE works..."
          />
        </FormField>
        <FormField label="Body preview">
          <Box padding="s" className="raise-course-card">
            <MarkdownView>{form.how_body}</MarkdownView>
          </Box>
        </FormField>
      </SpaceBetween>
    </Container>
  );
}

/* --------------------------------------------------------------- Contacts */
// Contact Us list: alias + role + order. Only the alias is stored; the public
// page resolves the name and badge photo from it. A table + add/edit modal,
// same shape as the lookup tabs.

// Default label for contacts with no role (mirrors the public page).
const CONTACT_UNGROUPED_LABEL = "Team";

function ContactsAdmin({ contacts, roleOrder, runWrite }) {
  const [editing, setEditing] = useState(null);
  const { busy } = useBusy();

  // Distinct existing roles feed the editor's autosuggest — pick an existing
  // section (role groups people on the public page) or type a new one.
  const roleOptions = useMemo(() => {
    const seen = new Set();
    (contacts || []).forEach((c) => {
      const r = (c.role || "").trim();
      if (r) seen.add(r);
    });
    return [...seen].sort().map((r) => ({ value: r }));
  }, [contacts]);

  return (
    <SpaceBetween size="l">
      <SectionOrderAdmin
        contacts={contacts}
        roleOrder={roleOrder}
        runWrite={runWrite}
      />
      <ContactsTable
        contacts={contacts}
        roleOptions={roleOptions}
        editing={editing}
        setEditing={setEditing}
        busy={busy}
        runWrite={runWrite}
      />
    </SpaceBetween>
  );
}

// The live roles reconciled against the stored order: stored roles first (in
// order, filtered to those still present), then any remaining live roles
// (alphabetical). Self-healing — stale entries drop out, new roles append.
function reconcileRoleOrder(contacts, roleOrder) {
  // Earliest member sort_order per live role — the fallback ordering key for
  // roles not in roleOrder, matching how the public Contact Us page orders
  // unlisted sections (so the editor reflects the live public ordering).
  const earliestOrder = new Map();
  (contacts || []).forEach((c) => {
    const label = (c.role || "").trim() || CONTACT_UNGROUPED_LABEL;
    const so = c.sort_order || 0;
    if (!earliestOrder.has(label) || so < earliestOrder.get(label)) {
      earliestOrder.set(label, so);
    }
  });
  const live = new Set(earliestOrder.keys());
  const ordered = [];
  (roleOrder || []).forEach((label) => {
    const key = (label || "").trim();
    if (key && live.has(key) && !ordered.includes(key)) {
      ordered.push(key);
    }
  });
  [...live]
    .filter((r) => !ordered.includes(r))
    .sort((a, b) => earliestOrder.get(a) - earliestOrder.get(b))
    .forEach((r) => ordered.push(r));
  return ordered;
}

// "Section order" editor — an up/down reorderable list of the live roles.
// Always rebuilt from the current contacts (so it never shows a dead role),
// pre-sorted by the stored order. Saving writes the roles in the chosen order
// to content(section='contacts', key='role_order'), which also prunes any
// stale entries.
function SectionOrderAdmin({ contacts, roleOrder, runWrite }) {
  const { busy } = useBusy();
  const reconciled = useMemo(
    () => reconcileRoleOrder(contacts, roleOrder),
    [contacts, roleOrder]
  );
  const [order, setOrder] = useState(reconciled);
  // Unambiguous serialization for comparing role-label lists — JSON handles any
  // characters in a label (quotes, commas, etc.) without a separator collision.
  const reconciledKey = JSON.stringify(reconciled);

  // Re-sync when the reconciled list changes (data reload after a write).
  React.useEffect(() => {
    setOrder(reconciled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconciledKey]);

  const move = (index, delta) => {
    setOrder((cur) => {
      const next = [...cur];
      const target = index + delta;
      if (target < 0 || target >= next.length) return cur;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const dirty = JSON.stringify(order) !== reconciledKey;

  const save = () =>
    runWrite(
      () =>
        upsertContent("contacts", [
          { key: "role_order", value: JSON.stringify(order), type: "text" },
        ]),
      "Section order saved.",
      null,
      "Save the section order?"
    );

  return (
    <Container
      header={
        <Header
          variant="h3"
          description="Order of the role sections on the public Contact Us page. Only roles currently in use are shown."
          actions={
            <Button variant="primary" loading={busy} disabled={busy || !dirty} onClick={save}>
              Save order
            </Button>
          }
        >
          Section order
        </Header>
      }
    >
      {order.length === 0 ? (
        <Box color="text-body-secondary">Add contacts with roles to order their sections.</Box>
      ) : (
        <SpaceBetween size="xs">
          {order.map((label, i) => (
            <div
              key={label}
              style={{ display: "flex", alignItems: "center", gap: "12px" }}
            >
              <Box variant="strong" fontSize="body-m">{`${i + 1}. ${label}`}</Box>
              <SpaceBetween direction="horizontal" size="xxs">
                <Button
                  variant="inline-icon"
                  iconName="angle-up"
                  ariaLabel={`Move ${label} up`}
                  disabled={busy || i === 0}
                  onClick={() => move(i, -1)}
                />
                <Button
                  variant="inline-icon"
                  iconName="angle-down"
                  ariaLabel={`Move ${label} down`}
                  disabled={busy || i === order.length - 1}
                  onClick={() => move(i, 1)}
                />
              </SpaceBetween>
            </div>
          ))}
        </SpaceBetween>
      )}
    </Container>
  );
}

// The contacts list table + add/edit modal.
function ContactsTable({ contacts, roleOptions, editing, setEditing, busy, runWrite }) {
  const { busyId } = useBusy();

  const { items: sortedRows, filter, tableSortProps } = useTableTools(contacts, {
    filterFields: ["alias", "role"],
    filterPlaceholder: "Find contacts",
  });

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={<Button variant="primary" disabled={busy} onClick={() => setEditing({})}>Add contact</Button>}
        >
          Contacts
        </Header>
      }
    >
      <Table
        items={sortedRows}
        {...tableSortProps}
        filter={filter}
        columnDefinitions={[
          { id: "alias", header: "Alias", cell: (c) => c.alias, sortingField: "alias" },
          { id: "role", header: "Role", cell: (c) => c.role || "", sortingField: "role" },
          { id: "sort", header: "Sort order", cell: (c) => c.sort_order ?? 0, sortingField: "sort_order" },
          {
            id: "actions",
            header: "Actions",
            minWidth: 150,
            width: 150,
            cell: (c) => (
              <div style={{ display: "flex", gap: "12px", whiteSpace: "nowrap" }}>
                <Button variant="inline-link" disabled={busy} onClick={() => setEditing(c)}>Edit</Button>
                <Button
                  variant="inline-link"
                  loading={busy && busyId === c.contact_id}
                  disabled={busy}
                  onClick={() => runWrite(() => deleteContact(c.contact_id), "Contact removed.", c.contact_id, `Remove contact "${c.alias}"? This can't be undone.`)}
                >
                  Remove
                </Button>
              </div>
            ),
          },
        ]}
        empty={<Box>No contacts.</Box>}
      />
      {editing ? (
        <ContactEditor
          contact={editing}
          existingContacts={contacts}
          roleOptions={roleOptions}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            const ok = await runWrite(() => upsertContact(data), "Contact saved.", null, "Save this contact?");
            if (ok) setEditing(null);
          }}
        />
      ) : null}
    </Container>
  );
}

function ContactEditor({ contact, existingContacts, roleOptions, onClose, onSave }) {
  const isNew = contact.contact_id == null;
  const [form, setForm] = useState({
    contact_id: contact.contact_id,
    alias: contact.alias || "",
    role: contact.role || "",
    sort_order:
      contact.sort_order != null
        ? String(contact.sort_order)
        : isNew
        ? nextSortOrder(existingContacts)
        : "",
  });
  const [error, setError] = useState(null);
  const { busy } = useBusy();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Alias autocomplete: as the admin types, look up matching active logins via
  // PAPI (driven by Autosuggest's onLoadItems, its dedicated typed-search hook)
  // so they pick a real alias rather than free-typing an invalid one.
  const [aliasOptions, setAliasOptions] = useState([]);
  const [aliasLoading, setAliasLoading] = useState(false);
  const aliasFetchRef = React.useRef(0);

  const fetchAliasOptions = React.useCallback((raw) => {
    const fragment = (raw || "").trim();
    if (fragment.length < 2) {
      // Invalidate any in-flight request so a longer-fragment response can't
      // repopulate options after the field was cleared back to < 2 chars.
      aliasFetchRef.current++;
      setAliasOptions([]);
      setAliasLoading(false);
      return;
    }
    // Guard against out-of-order responses: only the latest request wins.
    const seq = ++aliasFetchRef.current;
    setAliasLoading(true);
    getSuggestedLoginsV2(fragment, 10)
      .then((logins) => {
        if (seq !== aliasFetchRef.current) return;
        // PAPI's search endpoint returns login uppercased; Amazon logins are
        // canonically lowercase, so display them that way.
        setAliasOptions((logins || []).map((l) => ({ value: (l || "").toLowerCase() })));
      })
      .catch(() => {
        if (seq === aliasFetchRef.current) setAliasOptions([]);
      })
      .finally(() => {
        if (seq === aliasFetchRef.current) setAliasLoading(false);
      });
  }, []);

  const handleSave = () => {
    const alias = form.alias.trim();
    if (!alias) {
      setError("Alias is required.");
      return;
    }
    const role = form.role.trim();
    if (!role) {
      setError("Role is required.");
      return;
    }
    if (form.sort_order === "" || form.sort_order == null) {
      setError("Sort order is required.");
      return;
    }
    if (Number.isNaN(Number(form.sort_order))) {
      setError("Sort order must be a number.");
      return;
    }
    onSave({ ...form, alias, role });
  };

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={contact.contact_id ? "Edit contact" : "Add contact"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={busy} disabled={busy}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        {error ? <Box color="text-status-error" fontSize="body-s">{error}</Box> : null}
        <FormField label={<RequiredLabel>Alias</RequiredLabel>} description="Amazon login. Start typing to pick an active employee; name and badge photo are resolved from this.">
          <Autosuggest
            value={form.alias}
            onChange={({ detail }) => set("alias", detail.value)}
            onLoadItems={({ detail }) => fetchAliasOptions(detail.filteringText)}
            options={aliasOptions}
            statusType={aliasLoading ? "loading" : "finished"}
            loadingText="Searching logins..."
            placeholder="e.g. jeffbezos"
            empty="No matching logins"
            enteredTextLabel={(value) => `Use: "${value}"`}
            filteringType="manual"
            ariaLabel="Alias"
          />
        </FormField>
        <FormField
          label={<RequiredLabel>Role</RequiredLabel>}
          description="Groups contacts into sections on the public page. Pick an existing role or type a new one."
        >
          <Autosuggest
            value={form.role}
            onChange={({ detail }) => set("role", detail.value)}
            options={roleOptions || []}
            enteredTextLabel={(value) => `Use: "${value}"`}
            placeholder="e.g. Program Lead"
            empty="No existing roles — type to add one"
          />
        </FormField>
        <FormField label={<RequiredLabel>Sort order</RequiredLabel>} description="Auto-filled; edit to reorder.">
          <Input
            type="number"
            value={form.sort_order}
            onChange={({ detail }) => set("sort_order", detail.value)}
            placeholder="Auto"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

/* -------------------------------------------------- Generic lookup CRUD tab */
// A small table + add/edit modal for a lookup list (Providers, Platforms,
// Skill Sets, FAQ Categories). `nameKey` is the display field; `withSortOrder`
// adds a sort_order field (skill sets / categories). A delete is refused by the
// backend when the value is still in use (the in-use error is shown); otherwise
// the row is removed.
function LookupAdmin({ title, singular, items, idKey, nameKey, withSortOrder, onUpsert, onDelete, runWrite }) {
  const [editing, setEditing] = useState(null);
  const { busy, busyId } = useBusy();

  const columns = [
    { id: "name", header: "Name", cell: (r) => r[nameKey], sortingField: nameKey },
    ...(withSortOrder
      ? [{ id: "sort", header: "Sort order", cell: (r) => r.sort_order ?? 0, sortingField: "sort_order" }]
      : []),
    {
      id: "actions",
      header: "Actions",
      minWidth: 150,
      width: 150,
      cell: (r) => (
        <div style={{ display: "flex", gap: "12px", whiteSpace: "nowrap" }}>
          <Button variant="inline-link" disabled={busy} onClick={() => setEditing(r)}>Edit</Button>
          <Button
            variant="inline-link"
            loading={busy && busyId === r[idKey]}
            disabled={busy}
            onClick={() =>
              runWrite(() => onDelete(r[idKey]), `${title} option removed.`, r[idKey], `Remove this ${singular}? This can't be undone.`)
            }
          >
            Remove
          </Button>
        </div>
      ),
    },
  ];

  const { items: sortedItems, filter, tableSortProps } = useTableTools(items, {
    filterFields: [nameKey],
    filterPlaceholder: `Find ${title.toLowerCase()}`,
  });

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={<Button variant="primary" disabled={busy} onClick={() => setEditing({})}>Add {singular}</Button>}
        >
          {title}
        </Header>
      }
    >
      <Table items={sortedItems} {...tableSortProps} filter={filter} columnDefinitions={columns} empty={<Box>No {title.toLowerCase()}.</Box>} />
      {editing ? (
        <LookupEditor
          record={editing}
          singular={singular}
          idKey={idKey}
          nameKey={nameKey}
          withSortOrder={withSortOrder}
          existingItems={items}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            const ok = await runWrite(() => onUpsert(data), `${title} option saved.`, null, `Save this ${singular}?`);
            if (ok) setEditing(null);
          }}
        />
      ) : null}
    </Container>
  );
}

function LookupEditor({ record, singular, idKey, nameKey, withSortOrder, existingItems, onClose, onSave }) {
  const isNew = record[idKey] == null;
  const [form, setForm] = useState({
    name: record[nameKey] || "",
    // New rows prefill the next order (editable); existing rows keep theirs.
    sort_order:
      record.sort_order != null
        ? String(record.sort_order)
        : isNew
        ? nextSortOrder(existingItems)
        : "",
  });
  const [error, setError] = useState(null);
  const { busy } = useBusy();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = () => {
    const name = form.name.trim();
    if (!name) {
      setError("Name is required.");
      return;
    }
    // Map the generic "name" field to the payload key the backend expects:
    // skill sets use `name`, everything else uses `display_name`.
    const payload = { [idKey]: record[idKey] };
    if (nameKey === "name") payload.name = name;
    else payload.display_name = name;
    if (withSortOrder) payload.sort_order = form.sort_order;
    onSave(payload);
  };

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={record[idKey] ? `Edit ${singular}` : `Add ${singular}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={busy} disabled={busy}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        {error ? <Box color="text-status-error" fontSize="body-s">{error}</Box> : null}
        <FormField label={<RequiredLabel>Name</RequiredLabel>}>
          <Input
            value={form.name}
            onChange={({ detail }) => set("name", detail.value)}
            placeholder={`e.g. ${singular === "provider" ? "Skill Builder" : singular === "platform" ? "Amazon Quick Suite" : "Prompt & Context Mastery"}`}
          />
        </FormField>
        {withSortOrder ? (
          <FormField label="Sort order" description="Auto-filled; edit to reorder.">
            <Input
              type="number"
              value={form.sort_order}
              onChange={({ detail }) => set("sort_order", detail.value)}
              placeholder="Auto"
            />
          </FormField>
        ) : null}
      </SpaceBetween>
    </Modal>
  );
}

/* ------------------------------------------------------------------ Courses */

function CoursesAdmin({ curriculum, runWrite }) {
  const [editing, setEditing] = useState(null); // course object or {} for new
  const { busy, busyId } = useBusy();

  // Build cell options as "SkillSet — Tier" from the loaded curriculum.
  const tiersById = useMemo(() => {
    const m = {};
    (curriculum.tiers || []).forEach((t) => (m[t.tier_id] = t));
    return m;
  }, [curriculum.tiers]);
  const skillsById = useMemo(() => {
    const m = {};
    (curriculum.skill_sets || []).forEach((s) => (m[s.skill_set_id] = s));
    return m;
  }, [curriculum.skill_sets]);

  const skillSetOptions = useMemo(
    () =>
      [...(curriculum.skill_sets || [])]
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map((s) => ({ value: String(s.skill_set_id), label: s.name })),
    [curriculum.skill_sets]
  );
  const tierOptions = useMemo(
    () =>
      [...(curriculum.tiers || [])]
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map((t) => ({ value: String(t.tier_id), label: t.display_name })),
    [curriculum.tiers]
  );
  const providerOptions = useMemo(
    () =>
      [...(curriculum.providers || [])]
        .filter((p) => p.is_active !== false)
        .map((p) => ({ value: String(p.provider_id), label: p.display_name })),
    [curriculum.providers]
  );
  const cells = curriculum.cells || [];

  const rows = useMemo(() => {
    const all = [];
    (curriculum.cells || []).forEach((cell) => {
      (cell.courses || []).forEach((course) => {
        all.push({
          ...course,
          // Carry the cell's skill set + tier so the editor can pre-select both.
          skill_set_id: cell.skill_set_id,
          tier_id: cell.tier_id,
          skillName: skillsById[cell.skill_set_id]?.name || "",
          tierName: tiersById[cell.tier_id]?.display_name || "",
        });
      });
    });
    return all;
  }, [curriculum.cells, skillsById, tiersById]);

  const {
    items: sortedCourses,
    filter: courseFilter,
    tableSortProps: courseSortProps,
  } = useTableTools(rows, {
    filterFields: ["title", "skillName", "tierName", "provider_name", "duration_label", "status"],
    filterPlaceholder: "Find courses",
  });

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={<Button variant="primary" disabled={busy} onClick={() => setEditing({})}>Add course</Button>}
        >
          Courses
        </Header>
      }
    >
      <Table
        items={sortedCourses}
        {...courseSortProps}
        filter={courseFilter}
        resizableColumns
        columnDefinitions={[
          { id: "title", header: "Title", cell: (c) => c.title, minWidth: 200, sortingField: "title" },
          { id: "skillset", header: "Skill set", cell: (c) => c.skillName, minWidth: 180, sortingField: "skillName" },
          { id: "tier", header: "Tier", cell: (c) => c.tierName, minWidth: 120, sortingField: "tierName" },
          { id: "provider", header: "Provider", cell: (c) => c.provider_name || "", minWidth: 110, sortingField: "provider_name" },
          { id: "duration", header: "Duration", cell: (c) => c.duration_label || "", minWidth: 90, sortingField: "duration_minutes" },
          { id: "status", header: "Status", cell: (c) => c.status, minWidth: 110, sortingField: "status" },
          {
            id: "actions",
            header: "Actions",
            minWidth: 150,
            width: 150,
            cell: (c) => (
              <div style={{ display: "flex", gap: "12px", whiteSpace: "nowrap" }}>
                <Button variant="inline-link" disabled={busy} onClick={() => setEditing(c)}>Edit</Button>
                <Button
                  variant="inline-link"
                  loading={busy && busyId === c.course_id}
                  disabled={busy}
                  onClick={() =>
                    runWrite(() => deleteCourse(c.course_id), "Course deleted.", c.course_id, `Delete course "${c.title}"? This can't be undone.`)
                  }
                >
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        empty={<Box>No courses.</Box>}
      />
      {editing ? (
        <CourseEditor
          course={editing}
          skillSetOptions={skillSetOptions}
          tierOptions={tierOptions}
          providerOptions={providerOptions}
          cells={cells}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            const ok = await runWrite(() => upsertCourse(data), "Course saved.", null, "Save this course?");
            if (ok) setEditing(null);
          }}
        />
      ) : null}
    </Container>
  );
}

function CourseEditor({ course, skillSetOptions, tierOptions, providerOptions, cells, onClose, onSave }) {
  // Pre-select the provider by its id, falling back to a name match (mock rows
  // carry provider_name but may not carry provider_id).
  const initialProviderId =
    course.provider_id != null
      ? String(course.provider_id)
      : (providerOptions.find((o) => o.label === course.provider_name)?.value || "");
  const [form, setForm] = useState({
    course_id: course.course_id,
    skill_set_id: course.skill_set_id ? String(course.skill_set_id) : "",
    tier_id: course.tier_id ? String(course.tier_id) : "",
    provider_id: initialProviderId,
    title: course.title || "",
    duration_label: course.duration_label || "",
    duration_minutes: course.duration_minutes != null ? String(course.duration_minutes) : "",
    url: course.url || "",
    status: course.status || "AVAILABLE",
    eta_date: course.eta_date || "",
    is_mandatory: !!course.is_mandatory,
    sort_order: course.sort_order != null ? String(course.sort_order) : "",
  });
  const [error, setError] = useState(null);
  const { busy } = useBusy();
  // Track whether the admin hand-edited the order, so we stop auto-deriving it.
  const [sortTouched, setSortTouched] = useState(course.course_id != null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedSkillSet = skillSetOptions.find((o) => o.value === form.skill_set_id) || null;
  const selectedTier = tierOptions.find((o) => o.value === form.tier_id) || null;

  // If the course's saved provider is no longer in the active options (e.g. it
  // was later deactivated), add it back so the Select shows it rather than
  // rendering blank while the id is still held — which would otherwise let a
  // save slip through with a provider the UI shows as unselected.
  const providerOptionsWithCurrent = useMemo(() => {
    if (form.provider_id && !providerOptions.some((o) => o.value === form.provider_id)) {
      return [
        ...providerOptions,
        { value: form.provider_id, label: course.provider_name || "Current provider (inactive)" },
      ];
    }
    return providerOptions;
  }, [providerOptions, form.provider_id, course.provider_name]);
  const selectedProvider =
    providerOptionsWithCurrent.find((o) => o.value === form.provider_id) || null;

  // The cell this (skill set, tier) maps to, and the courses already in it.
  const currentCell = (cells || []).find(
    (c) =>
      String(c.skill_set_id) === form.skill_set_id &&
      String(c.tier_id) === form.tier_id
  );

  // For a new course, prefill sort_order as "next in this cell" whenever the
  // cell changes and the admin hasn't overridden it.
  React.useEffect(() => {
    if (sortTouched || !currentCell) return;
    setForm((f) => ({ ...f, sort_order: nextSortOrder(currentCell.courses) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCell && currentCell.cell_id, sortTouched]);

  // ETA only applies to a course that is still being built. When AVAILABLE, the
  // field is disabled and any value is dropped on save.
  const etaApplies = form.status === "COMING_SOON";

  const handleSave = () => {
    // Required-field checks, client-side, before any backend call.
    if (!form.skill_set_id || !form.tier_id) {
      setError("Select both a skill set and a tier.");
      return;
    }
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!form.provider_id) {
      setError("Select a provider.");
      return;
    }
    // URL is required for an available course; a being-built course may not
    // have one yet. When present, it must be a valid https link.
    const url = form.url.trim();
    if (!etaApplies && !url) {
      setError("URL is required for an available course.");
      return;
    }
    if (url && !isValidHttpsUrl(url)) {
      setError("Enter a valid https:// URL.");
      return;
    }
    if (form.duration_minutes && Number.isNaN(Number(form.duration_minutes))) {
      setError("Duration minutes must be a number.");
      return;
    }
    // Send skill_set_id + tier_id; the backend resolves the cell and creates it
    // if this (skill set, tier) has none yet (e.g. a newly added skill set).
    // Pass cell_id too when we already know it, to skip the lookup.
    onSave({
      ...form,
      skill_set_id: form.skill_set_id,
      tier_id: form.tier_id,
      cell_id: currentCell ? String(currentCell.cell_id) : "",
      // Drop ETA entirely for an available course so we never send a stale date.
      eta_date: etaApplies ? form.eta_date : "",
    });
  };

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={course.course_id ? "Edit course" : "Add course"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={busy} disabled={busy}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        {error ? (
          <Box color="text-status-error" fontSize="body-s">{error}</Box>
        ) : null}
        <ColumnLayout columns={2}>
          <FormField label={<RequiredLabel>Skill set</RequiredLabel>}>
            <Select
              selectedOption={selectedSkillSet}
              onChange={({ detail }) => set("skill_set_id", detail.selectedOption.value)}
              options={skillSetOptions}
              placeholder="Select a skill set"
            />
          </FormField>
          <FormField label={<RequiredLabel>Tier</RequiredLabel>}>
            <Select
              selectedOption={selectedTier}
              onChange={({ detail }) => set("tier_id", detail.selectedOption.value)}
              options={tierOptions}
              placeholder="Select a tier"
            />
          </FormField>
        </ColumnLayout>
        <FormField label={<RequiredLabel>Title</RequiredLabel>}>
          <Input
            value={form.title}
            onChange={({ detail }) => set("title", detail.value)}
            placeholder="e.g. AI Fundamentals - AIFUN"
          />
        </FormField>
        <ColumnLayout columns={2}>
          <FormField
            label={<RequiredLabel>Provider</RequiredLabel>}
            description="Manage the list in the Providers tab."
          >
            <Select
              selectedOption={selectedProvider}
              onChange={({ detail }) => set("provider_id", detail.selectedOption.value)}
              options={providerOptionsWithCurrent}
              placeholder="Select a provider"
              empty="No providers — add one in the Providers tab"
            />
          </FormField>
          <FormField label="Duration label">
            <Input
              value={form.duration_label}
              onChange={({ detail }) => set("duration_label", detail.value)}
              placeholder="e.g. 45m, 1h 6m, 4 mins"
            />
          </FormField>
          <FormField
            label="Duration minutes (for rollups)"
            description="Numeric minutes; powers the tier time totals."
          >
            <Input
              type="number"
              value={form.duration_minutes}
              onChange={({ detail }) => set("duration_minutes", detail.value)}
              placeholder="e.g. 45"
            />
          </FormField>
          <FormField label="Sort order" description="Auto-filled; edit to reorder.">
            <Input
              type="number"
              value={form.sort_order}
              onChange={({ detail }) => {
                setSortTouched(true);
                set("sort_order", detail.value);
              }}
              placeholder="Auto"
            />
          </FormField>
        </ColumnLayout>
        <FormField
          label={etaApplies ? "URL" : <RequiredLabel>URL</RequiredLabel>}
          description={
            etaApplies
              ? "Optional while the course is coming soon; must be a valid https:// link."
              : "Required. Must be a valid https:// link."
          }
        >
          <Input
            value={form.url}
            onChange={({ detail }) => set("url", detail.value)}
            placeholder="https://..."
          />
        </FormField>
        <ColumnLayout columns={2}>
          <FormField label="Status">
            <Select
              selectedOption={{ value: form.status, label: form.status }}
              onChange={({ detail }) => {
                const status = detail.selectedOption.value;
                // Switching to AVAILABLE clears any ETA — it no longer applies.
                setForm((f) => ({
                  ...f,
                  status,
                  eta_date: status === "COMING_SOON" ? f.eta_date : "",
                }));
              }}
              options={[
                { value: "AVAILABLE", label: "AVAILABLE" },
                { value: "COMING_SOON", label: "COMING_SOON" },
              ]}
            />
          </FormField>
          <FormField
            label="ETA date"
            description={
              etaApplies
                ? "Optional; when a coming-soon course is expected."
                : "Only applies while the course is COMING_SOON."
            }
          >
            <DatePicker
              value={etaApplies ? form.eta_date || "" : ""}
              disabled={!etaApplies}
              onChange={({ detail }) => set("eta_date", detail.value)}
              placeholder="YYYY/MM/DD"
              openCalendarAriaLabel={(selectedDate) =>
                "Choose ETA date" + (selectedDate ? `, selected ${selectedDate}` : "")
              }
            />
          </FormField>
        </ColumnLayout>
        <Checkbox checked={form.is_mandatory} onChange={({ detail }) => set("is_mandatory", detail.checked)}>
          Mandatory
        </Checkbox>
      </SpaceBetween>
    </Modal>
  );
}

/* ------------------------------------------------------------------- Matrix */
// The skill-set x tier grid. Each combination is a "cell" carrying the short
// tagline shown under it on the public matrix. Lists every combination (even
// those without a persisted cell yet) so taglines can be set for empty cells;
// saving one that has no cell lazily creates it (same as adding a course).

function MatrixAdmin({ curriculum, runWrite }) {
  const [editing, setEditing] = useState(null);
  const { busy, busyId } = useBusy();

  const tiers = useMemo(
    () =>
      [...(curriculum.tiers || [])].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
      ),
    [curriculum.tiers]
  );
  const skillSets = useMemo(
    () =>
      [...(curriculum.skill_sets || [])].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
      ),
    [curriculum.skill_sets]
  );

  // Index persisted cells by "skillSetId#tierId".
  const cellIndex = useMemo(() => {
    const m = {};
    (curriculum.cells || []).forEach((c) => {
      m[`${c.skill_set_id}#${c.tier_id}`] = c;
    });
    return m;
  }, [curriculum.cells]);

  // One row per (skill set x tier) — existing cell or a placeholder for one
  // that hasn't been created yet.
  const rows = useMemo(() => {
    const out = [];
    skillSets.forEach((s) => {
      tiers.forEach((t) => {
        const cell = cellIndex[`${s.skill_set_id}#${t.tier_id}`];
        out.push({
          key: `${s.skill_set_id}#${t.tier_id}`,
          skill_set_id: s.skill_set_id,
          tier_id: t.tier_id,
          skillName: s.name,
          tierName: t.display_name,
          cell_id: cell ? cell.cell_id : null,
          tagline: cell ? cell.tagline || "" : "",
          courseCount: cell && cell.courses ? cell.courses.length : 0,
        });
      });
    });
    return out;
  }, [skillSets, tiers, cellIndex]);

  const { items: sortedRows, filter, tableSortProps } = useTableTools(rows, {
    filterFields: ["skillName", "tierName", "tagline"],
    filterPlaceholder: "Find cells",
  });

  return (
    <Container header={<Header variant="h3">Matrix cells (taglines)</Header>}>
      <Table
        items={sortedRows}
        {...tableSortProps}
        filter={filter}
        resizableColumns
        columnDefinitions={[
          { id: "skillset", header: "Skill set", cell: (r) => r.skillName, minWidth: 180, sortingField: "skillName" },
          { id: "tier", header: "Tier", cell: (r) => r.tierName, minWidth: 120, sortingField: "tierName" },
          {
            id: "tagline",
            header: "Tagline",
            cell: (r) =>
              r.tagline || <Box color="text-status-inactive">— none —</Box>,
            minWidth: 260,
            sortingField: "tagline",
          },
          { id: "courses", header: "Courses", cell: (r) => r.courseCount, minWidth: 90, sortingField: "courseCount" },
          {
            id: "actions",
            header: "Actions",
            minWidth: 110,
            width: 110,
            cell: (r) => (
              <Button variant="inline-link" disabled={busy} onClick={() => setEditing(r)}>
                Edit
              </Button>
            ),
          },
        ]}
        empty={<Box>Add skill sets and tiers first.</Box>}
      />
      {editing ? (
        <CellEditor
          cell={editing}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            const ok = await runWrite(() => upsertCell(data), "Tagline saved.", null, "Save this tagline?");
            if (ok) setEditing(null);
          }}
        />
      ) : null}
    </Container>
  );
}

function CellEditor({ cell, onClose, onSave }) {
  const [tagline, setTagline] = useState(cell.tagline || "");
  const { busy } = useBusy();

  const handleSave = () =>
    onSave({
      cell_id: cell.cell_id != null ? String(cell.cell_id) : "",
      skill_set_id: String(cell.skill_set_id),
      tier_id: String(cell.tier_id),
      tagline,
    });

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={`Tagline · ${cell.skillName} / ${cell.tierName}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={busy} disabled={busy}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField
        label="Tagline"
        description="Short line shown under this cell on the public matrix. Leave blank for none."
      >
        <Input
          value={tagline}
          onChange={({ detail }) => setTagline(detail.value)}
          placeholder="e.g. Ask direct questions; get useful answers."
        />
      </FormField>
    </Modal>
  );
}

/* ----------------------------------------------------------------- Calendar */

function CalendarAdmin({ calendar, runWrite }) {
  const [editing, setEditing] = useState(null);
  const { busy, busyId } = useBusy();
  const platformOptions = (calendar.platforms || []).map((p) => ({
    value: String(p.platform_id),
    label: p.display_name,
  }));
  const rows = calendar.sessions || [];
  const { items: sortedRows, filter, tableSortProps } = useTableTools(rows, {
    filterFields: ["title", "session_date", "platform_name", "region_label"],
    filterPlaceholder: "Find sessions",
  });

  return (
    <Container
      header={
        <Header variant="h3" actions={<Button variant="primary" disabled={busy} onClick={() => setEditing({})}>Add session</Button>}>
          Calendar sessions
        </Header>
      }
    >
      <Table
        items={sortedRows}
        {...tableSortProps}
        filter={filter}
        columnDefinitions={[
          { id: "title", header: "Title", cell: (s) => s.title, sortingField: "title" },
          { id: "date", header: "Date", cell: (s) => s.session_date, sortingField: "session_date" },
          { id: "platform", header: "Platform", cell: (s) => s.platform_name || "", sortingField: "platform_name" },
          {
            id: "state",
            header: "State",
            cell: (s) => sessionState(s.session_date),
            sortingField: "session_date",
          },
          {
            id: "actions",
            header: "Actions",
            minWidth: 150,
            width: 150,
            cell: (s) => (
              <div style={{ display: "flex", gap: "12px", whiteSpace: "nowrap" }}>
                <Button variant="inline-link" disabled={busy} onClick={() => setEditing(s)}>Edit</Button>
                <Button variant="inline-link" loading={busy && busyId === s.session_id} disabled={busy} onClick={() => runWrite(() => deleteSession(s.session_id), "Session deleted.", s.session_id, `Delete session "${s.title}"? This can't be undone.`)}>Delete</Button>
              </div>
            ),
          },
        ]}
        empty={<Box>No sessions.</Box>}
      />
      {editing ? (
        <SessionEditor
          session={editing}
          platformOptions={platformOptions}
          existingSessions={rows}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            const ok = await runWrite(() => upsertSession(data), "Session saved.", null, "Save this session?");
            if (ok) setEditing(null);
          }}
        />
      ) : null}
    </Container>
  );
}

function SessionEditor({ session, platformOptions, existingSessions, onClose, onSave }) {
  const [form, setForm] = useState({
    session_id: session.session_id,
    title: session.title || "",
    platform_id: session.platform_id ? String(session.platform_id) : "",
    session_date: session.session_date || "",
    region_label: session.region_label || "",
    recording_url: session.recording_url || "",
    sort_order: session.sort_order != null ? String(session.sort_order) : "",
  });
  const [error, setError] = useState(null);
  const { busy } = useBusy();
  const [sortTouched, setSortTouched] = useState(session.session_id != null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedPlatform = platformOptions.find((o) => o.value === form.platform_id) || null;

  // For a new session, prefill sort_order as "next among sessions on that date"
  // whenever the date changes and the admin hasn't overridden it.
  React.useEffect(() => {
    if (sortTouched || !form.session_date) return;
    const sameDate = (existingSessions || []).filter(
      (s) => s.session_date === form.session_date
    );
    setForm((f) => ({ ...f, sort_order: nextSortOrder(sameDate) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.session_date, sortTouched]);

  const handleSave = () => {
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!form.platform_id) {
      setError("Select a platform.");
      return;
    }
    if (!form.session_date) {
      setError("Date is required.");
      return;
    }
    // Recording URL is optional, but when present must be a valid https link.
    if (form.recording_url.trim() && !isValidHttpsUrl(form.recording_url)) {
      setError("Recording URL must be a valid https:// link.");
      return;
    }
    onSave(form);
  };

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={session.session_id ? "Edit session" : "Add session"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={busy} disabled={busy}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        {error ? (
          <Box color="text-status-error" fontSize="body-s">{error}</Box>
        ) : null}
        <FormField label={<RequiredLabel>Title</RequiredLabel>}>
          <Input
            value={form.title}
            onChange={({ detail }) => set("title", detail.value)}
            placeholder="e.g. AI Spotlight: CRISP"
          />
        </FormField>
        <ColumnLayout columns={2}>
          <FormField label={<RequiredLabel>Platform</RequiredLabel>}>
            <Select
              selectedOption={selectedPlatform}
              onChange={({ detail }) => set("platform_id", detail.selectedOption.value)}
              options={platformOptions}
              placeholder="Select a platform"
            />
          </FormField>
          <FormField label={<RequiredLabel>Date</RequiredLabel>}>
            <DatePicker
              value={form.session_date || ""}
              onChange={({ detail }) => set("session_date", detail.value)}
              placeholder="YYYY/MM/DD"
              openCalendarAriaLabel={(selectedDate) =>
                "Choose session date" + (selectedDate ? `, selected ${selectedDate}` : "")
              }
            />
          </FormField>
        </ColumnLayout>
        <FormField label="Description" description="Free text; e.g. regions and times as they should display.">
          <Input
            value={form.region_label}
            onChange={({ detail }) => set("region_label", detail.value)}
            placeholder="e.g. AMER/EMEA/LATAM · 08:00 PT / 16:00 UK"
          />
        </FormField>
        <ColumnLayout columns={2}>
          <FormField
            label="State"
            description="Set automatically from the date — past is delivered."
          >
            <Input
              value={sessionState(form.session_date) || "—"}
              readOnly
            />
          </FormField>
          <FormField label="Sort order" description="Auto-filled; edit to reorder.">
            <Input
              type="number"
              value={form.sort_order}
              onChange={({ detail }) => {
                setSortTouched(true);
                set("sort_order", detail.value);
              }}
              placeholder="Auto"
            />
          </FormField>
        </ColumnLayout>
        <FormField label="Recording URL" description="Optional; must be a valid https:// link to the published recording.">
          <Input
            value={form.recording_url}
            onChange={({ detail }) => set("recording_url", detail.value)}
            placeholder="https://broadcast.amazon.com/videos/..."
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

/* --------------------------------------------------------------------- FAQ */

function FaqAdmin({ faq, runWrite }) {
  const [editing, setEditing] = useState(null);
  const { busy, busyId } = useBusy();
  const categories = faq.categories || [];
  const rows = useMemo(() => {
    const all = [];
    categories.forEach((cat) =>
      (cat.faqs || []).forEach((f) => all.push({ ...f, categoryName: cat.name }))
    );
    (faq.uncategorized || []).forEach((f) => all.push({ ...f, categoryName: "" }));
    return all;
  }, [categories, faq.uncategorized]);

  const categoryOptions = categories.map((c) => ({
    value: String(c.category_id),
    label: c.name,
  }));

  const { items: sortedRows, filter, tableSortProps } = useTableTools(rows, {
    filterFields: ["question", "answer", "categoryName"],
    filterPlaceholder: "Find FAQs",
  });

  return (
    <Container
      header={
        <Header variant="h3" actions={<Button variant="primary" disabled={busy} onClick={() => setEditing({})}>Add FAQ</Button>}>
          FAQ
        </Header>
      }
    >
      <Table
        items={sortedRows}
        {...tableSortProps}
        filter={filter}
        columnDefinitions={[
          { id: "q", header: "Question", cell: (f) => f.question, sortingField: "question" },
          { id: "cat", header: "Category", cell: (f) => f.categoryName, sortingField: "categoryName" },
          {
            id: "actions",
            header: "Actions",
            minWidth: 150,
            width: 150,
            cell: (f) => (
              <div style={{ display: "flex", gap: "12px", whiteSpace: "nowrap" }}>
                <Button variant="inline-link" disabled={busy} onClick={() => setEditing(f)}>Edit</Button>
                <Button variant="inline-link" loading={busy && busyId === f.faq_id} disabled={busy} onClick={() => runWrite(() => deleteFaq(f.faq_id), "FAQ deleted.", f.faq_id, "Delete this FAQ? This can't be undone.")}>Delete</Button>
              </div>
            ),
          },
        ]}
        empty={<Box>No FAQ entries.</Box>}
      />
      {editing ? (
        <FaqEditor
          faq={editing}
          categoryOptions={categoryOptions}
          existingFaqs={rows}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            const ok = await runWrite(() => upsertFaq(data), "FAQ saved.", null, "Save this FAQ?");
            if (ok) setEditing(null);
          }}
        />
      ) : null}
    </Container>
  );
}

function FaqEditor({ faq, categoryOptions, existingFaqs, onClose, onSave }) {
  const [form, setForm] = useState({
    faq_id: faq.faq_id,
    category_id: faq.category_id ? String(faq.category_id) : "",
    question: faq.question || "",
    answer: faq.answer || "",
    sort_order: faq.sort_order != null ? String(faq.sort_order) : "",
  });
  const [error, setError] = useState(null);
  const { busy } = useBusy();
  const [sortTouched, setSortTouched] = useState(faq.faq_id != null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedCategory = categoryOptions.find((o) => o.value === form.category_id) || null;

  // For a new FAQ, prefill sort_order as "next within the chosen category"
  // (blank category = uncategorized) unless the admin overrides it.
  React.useEffect(() => {
    if (sortTouched) return;
    const sameCat = (existingFaqs || []).filter(
      (f) => String(f.category_id || "") === String(form.category_id || "")
    );
    setForm((f) => ({ ...f, sort_order: nextSortOrder(sameCat) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category_id, sortTouched]);

  const handleSave = () => {
    if (!form.question.trim()) {
      setError("Question is required.");
      return;
    }
    if (!form.answer.trim()) {
      setError("Answer is required.");
      return;
    }
    onSave(form);
  };

  return (
    <Modal
      visible
      size="large"
      onDismiss={onClose}
      header={faq.faq_id ? "Edit FAQ" : "Add FAQ"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={busy} disabled={busy}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        {error ? (
          <Box color="text-status-error" fontSize="body-s">{error}</Box>
        ) : null}
        <ColumnLayout columns={2}>
          <FormField label="Category (optional)">
            <Select
              selectedOption={selectedCategory}
              onChange={({ detail }) => set("category_id", detail.selectedOption.value)}
              options={categoryOptions}
              placeholder="Uncategorized"
            />
          </FormField>
          <FormField label="Sort order" description="Auto-filled; edit to reorder.">
            <Input
              type="number"
              value={form.sort_order}
              onChange={({ detail }) => {
                setSortTouched(true);
                set("sort_order", detail.value);
              }}
              placeholder="Auto"
            />
          </FormField>
        </ColumnLayout>
        <FormField label={<RequiredLabel>Question</RequiredLabel>}>
          <Input
            value={form.question}
            onChange={({ detail }) => set("question", detail.value)}
            placeholder="e.g. When do certifications launch?"
          />
        </FormField>
        <FormField
          label={<RequiredLabel>Answer (Markdown)</RequiredLabel>}
          description="Supports **bold**, lists, and [links](https://...)."
        >
          <Textarea
            value={form.answer}
            onChange={({ detail }) => set("answer", detail.value)}
            rows={6}
            placeholder="Write the answer in Markdown..."
          />
        </FormField>
        <FormField label="Preview">
          <Box padding="s" className="raise-course-card">
            <MarkdownView>{form.answer}</MarkdownView>
          </Box>
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
